import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { orderService, internalOrderService } from '../services/api';
import { normalize, getPaymentMethodLabel, getPaymentBadgeClass, getElectronicLabel, getElectronicBadgeClass, resolveElectronicType, detectProviderFromString, getProviderHint, isCreditOrder } from '../utils/payments';
import { extractRecipientDataFromNotes } from '../utils/recipientExtractor';
import { printInternalVoucher } from '../utils/printInternalVoucher';

/* Helpers de visualización de métodos de pago (producto y envío)
   Nota: normalize, getPaymentMethodLabel y getPaymentBadgeClass se importan desde utils/payments */
function getShippingPayLabel(method) {
  const v = normalize(method);
  if (v === 'contraentrega' || v === 'por_cobrar') return 'Contraentrega (paga cliente)';
  if (v === 'contado' || v === 'pagado' || v === 'prepagado') return 'Contado (paga empresa)';
  if (!v) return 'No especificado';
  return method;
}
function getShippingBadgeClass(method) {
  const v = normalize(method);
  if (v === 'contraentrega' || v === 'por_cobrar') return 'bg-orange-100 text-orange-800';
  if (v === 'contado' || v === 'pagado' || v === 'prepagado') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-800';
}

/* Proveedor de pago electrónico:
   Usar helpers centralizados de utils/payments:
   - resolveElectronicType, getElectronicLabel, getElectronicBadgeClass,
     detectProviderFromString, getProviderHint */

// Componente CustomDropdown para reemplazar select nativo
const CustomDropdown = ({ value, onChange, options, placeholder, required }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value);


  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
        style={{ zIndex: 1 }}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Icons.ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors ${option.value === value ? 'bg-blue-50 text-blue-900' : ''
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// Helpers robustos para obtener precios desde diferentes formas (unit_price, price, subtotal, total)
const toMoneyNumber = (v) => {
  if (v === null || v === undefined) return 0;
  const s = typeof v === 'number' ? String(v) : String(v).trim();
  const cleaned = s.replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};
const getUnitPrice = (item) => {
  const qty = Number(item?.quantity || 1) || 1;
  const candidates = [
    item?.unit_price,
    item?.price,
    item?.unitPrice,
    item?.precio,
    item?.value,
    (item?.total ?? item?.total_amount)
      ? toMoneyNumber(item?.total ?? item?.total_amount) / qty
      : null
  ];
  for (const c of candidates) {
    const n = toMoneyNumber(c);
    if (n > 0) return n;
  }
  // Si todo falla, devolver 0
  return 0;
};
const getSubtotal = (item) => {
  const qty = Number(item?.quantity || 1) || 1;
  const candidates = [
    item?.subtotal,
    item?.total,
    item?.total_amount,
    getUnitPrice(item) * qty
  ];
  for (const c of candidates) {
    const n = toMoneyNumber(c);
    if (n > 0) return n;
  }
  return 0;
};

const LogisticsModal = ({ isOpen, onClose, order, onProcess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    shippingMethod: '',
    transportCompany: '',
    trackingNumber: '',
    shippingDate: '',
    shippingPaymentMethod: 'contado',
    deliveryFee: '',
    deliveryFeeExempt: false,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [initialCarrierSet, setInitialCarrierSet] = useState(false);
  // UI antibobos: bloquear/forzar decisiones rápidas para domicilio
  const [lockDeliveryFee, setLockDeliveryFee] = useState(false);
  // Secciones plegables para reducir ruido visual
  const [showItems, setShowItems] = useState(false);
  const [showInternalItems, setShowInternalItems] = useState(false);
  const [showSiigoNotes, setShowSiigoNotes] = useState(false);
  const [internalOrders, setInternalOrders] = useState([]);
  const [internalOrdersLoading, setInternalOrdersLoading] = useState(false);

  // Métodos de envío considerados "locales" (no requieren transportadora)
  // Robustez: aceptar variantes con espacios, guiones y acentos (ej. "Domicilio local", "mensajería urbana", etc.)
  const LOCAL_METHODS = new Set(['domicilio', 'domicilio_ciudad', 'mensajeria_local', 'mensajeria_urbana', 'domicilio_local']);
  const normalizeMethod = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // quitar acentos
      .replace(/\s+/g, '_') // espacios -> underscore
      .replace(/-+/g, '_'); // guiones -> underscore
  const isLocalMethod = (m) => {
    const v = normalizeMethod(m);
    if (LOCAL_METHODS.has(v)) return true;
    // Cualquier "domicilio" que NO sea nacional/internacional se considera local
    if (v.includes('domicilio') && !v.includes('nacional') && !v.includes('internacional')) return true;
    // Mensajería urbana/local
    if (v.includes('mensajeria') && !v.includes('nacional')) return true;
    return false;
  };

  // Abrir por defecto las secciones plegables para el rol Logística
  React.useEffect(() => {
    if (!isOpen) return;
    const role = String(user?.role || '').toLowerCase();
    const hasLogRole = role === 'logistica' || (Array.isArray(user?.roles) && user.roles.some(r => String(r.role_name || '').toLowerCase() === 'logistica'));
    if (hasLogRole) {
      setShowItems(true);
      setShowSiigoNotes(true);
    }
  }, [isOpen, user]);

  // Función para extraer datos del destinatario desde las observaciones y notas de SIIGO
  const extractRecipientData = (observations, notes) => {
    const data = {};
    // Unir todo el texto disponible
    const fullText = [observations, notes].filter(Boolean).join('\n');

    // Lista de etiquetas conocidas para el lookahead (detenerse si encontramos otra etiqueta)
    const labelsPattern = '(?:NOMBRE|CLIENTE|NIT|TEL[ÉE]FONO|CELULAR|TEL|CEL|DIRECCI[ÓO]N|DIR|CIUDAD|MUNICIPIO|DESTINO|DEPARTAMENTO|DEPTO|DPTO|DEP|FORMA|MEDIO|M[ÉE]TODO|ESTADO|NOTA)';

    // Helper para crear regex robusto que busque: ETIQUETA + separador opcional + VALOR + (hasta sgte etiqueta o fin)
    const extractValue = (keyPattern) => {
      // Regex explicado:
      // 1. (?:${keyPattern}) : La clave que buscamos (no capturante)
      // 2. \s*[:\-]?\s*     : Separador opcional (: o -) y espacios alrededor
      // 3. (.+?)            : Captura el valor (non-greedy)
      // 4. Lookahead relajado: permite 0 espacios antes de la siguiente etiqueta para casos pegados
      const regex = new RegExp(`(?:${keyPattern})\\s*[:\\-]?\\s*(.+?)(?=\\s*(?:${labelsPattern})\\s*[:\\-]|$)`, 'is');
      const match = fullText.match(regex);
      return match ? match[1].trim() : null;
    };

    // Extracciones

    // NOMBRE
    const name = extractValue('NOMBRE|CLIENTE');
    if (name && name !== 'ESTADO DE PAGO') data.name = name;

    // TELÉFONO
    const phone = extractValue('TEL[ÉE]FONO|CELULAR|TEL|CEL');
    if (phone) data.phone = phone;

    // DIRECCIÓN
    const address = extractValue('DIRECCI[ÓO]N|DIR');
    if (address) data.address = address;

    // CIUDAD
    // Intento 1: Usar extractValue general
    let city = extractValue('CIUDAD(?:\\s*DESTINO)?|MUNICIPIO|DESTINO');

    // Intento 2: Regex específico y simple si falla el general (para casos pegados o con formato raro)
    if (!city) {
      const mCity = fullText.match(/(?:CIUDAD|MUNICIPIO)\s*[:\-]\s*([^:\n]+?)\s*(?:DIRECCI[ÓO]N|DIR|TEL|CEL|NIT|FORMA|MEDIO|NOTA|$)/i);
      if (mCity) city = mCity[1];
    }

    if (city) {
      // Limpiar si viene con departamento (ej: "Manizales, Caldas" -> "Manizales")
      // También limpiar si capturó "MEDELLIN" (por defecto) y queremos asegurar que no sea ruido
      data.city = city.split(/[,.\-]/)[0].trim();
    }

    // DEPARTAMENTO
    let dept = extractValue('DEPARTAMENTO|DEPTO|DPTO|DEP');

    if (!dept) {
      const mDep = fullText.match(/(?:DEPARTAMENTO|DEPTO)\s*[:\-]\s*([^:\n]+?)\s*(?:CIUDAD|MUNICIPIO|DIRECCI[ÓO]N|DIR|TEL|CEL|NIT|$)/i);
      if (mDep) dept = mDep[1];
    }

    if (dept) {
      data.department = dept.split(/[,.\-]/)[0].trim();
    }

    // NIT
    const nit = extractValue('NIT');
    if (nit) data.nit = nit;

    // FORMA DE PAGO DE ENVIO
    const shippingPay = extractValue('FORMA\\s+DE\\s+PAGO\\s+DE\\s+ENVIO');
    if (shippingPay) data.shippingPaymentMethod = shippingPay;

    // FORMA DE PAGO (General)
    const payMethod = extractValue('MEDIO\\s+(?:DE\\s+)?PAGO|FORMA\\s+(?:DE\\s+)?PAGO|M[ÉE]TODO\\s+(?:DE\\s+)?PAGO');
    if (payMethod) data.paymentMethod = payMethod;

    // Detectar proveedor en el texto completo
    const providerInText = detectProviderFromString(fullText);
    if (providerInText && !data.electronicProvider) {
      data.electronicProvider = providerInText;
    }

    return Object.keys(data).length > 0 ? data : null;
  };

  // Extraer datos cuando cambie el pedido - priorizar observaciones de SIIGO
  React.useEffect(() => {
    if (order) {
      const extracted = extractRecipientDataFromNotes([order?.siigo_observations, order?.notes].filter(Boolean).join('\n'));
      setExtractedData(extracted);
      console.log('LogisticsModal: extractedData (on order change)', extracted);

      // Cargar método de envío preseleccionado por el facturador
      setFormData(prev => ({
        ...prev,
        // Siempre limpiar selección previa al cargar un pedido para evitar preselecciones residuales
        transportCompany: '',
        trackingNumber: '',
        shippingMethod: order.delivery_method || '',
        // Cargar automáticamente el método de pago de envío
        // PRIORIDAD 1: Desde la base de datos
        // PRIORIDAD 2: Desde los datos extraídos
        // DEFAULT: 'contado' (Solicitud de usuario 2024-01-22: Siempre asumimos contado/facturado)
        shippingPaymentMethod: order.shipping_payment_method || extractedData?.shippingPaymentMethod?.toLowerCase() || 'contado',
        notes: order.notes || ''
      }));

      // Fetch internal orders
      const fetchInternalOrders = async () => {
        setInternalOrdersLoading(true);
        try {
          const response = await internalOrderService.getByParent(order.id);
          if (response && response.success) {
            setInternalOrders(response.data || []);
          }
        } catch (error) {
          console.error('Error fetching internal orders:', error);
        } finally {
          setInternalOrdersLoading(false);
        }
      };

      fetchInternalOrders();
    } else {
      setExtractedData(null);
      // Reset form cuando no hay pedido
      setFormData({
        shippingMethod: '',
        transportCompany: '',
        trackingNumber: '',
        shippingPaymentMethod: 'contado',
        deliveryFee: '',
        deliveryFeeExempt: false,
        notes: ''
      });
    }
  }, [order]);

  // Al abrir el modal, limpiar selección de transportadora/tracking para evitar valores residuales
  React.useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        transportCompany: '',
        trackingNumber: ''
      }));
    }
  }, [isOpen]);

  // Recalcular extracción cuando se abre el modal, por si el estado se montó antes de tener observaciones
  React.useEffect(() => {
    if (isOpen && order) {
      const extracted = extractRecipientDataFromNotes([order?.siigo_observations, order?.notes].filter(Boolean).join('\n'));
      setExtractedData(extracted);
      console.log('LogisticsModal: extractedData (on open)', extracted);
    }
  }, [isOpen, order]);

  // Recalcular extracción cuando cambien Observaciones/Notas
  // (cubre el caso donde el padre muta propiedades en el mismo objeto 'order' sin cambiar la referencia)
  React.useEffect(() => {
    const keyObs = order?.siigo_observations ?? '';
    const keyNotes = order?.notes ?? '';
    if (keyObs || keyNotes) {
      const extracted = extractRecipientDataFromNotes([keyObs, keyNotes].filter(Boolean).join('\n'));
      setExtractedData(extracted);
      console.log('LogisticsModal: extractedData (on obs/notes change)', extracted);
    }
  }, [order?.siigo_observations, order?.notes]);

  // Estados para métodos de envío dinámicos
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);

  // Efecto para asegurar que el método de envío se establezca correctamente después de cargar los métodos
  React.useEffect(() => {
    if (order && shippingMethods.length > 0 && !loadingMethods) {
      // Solo actualizar si el método existe en las opciones disponibles
      if (order.delivery_method && shippingMethods.find(m => m.value === order.delivery_method)) {
        setFormData(prev => ({
          ...prev,
          shippingMethod: order.delivery_method
        }));
      }
    }
  }, [order, shippingMethods, loadingMethods]);

  // Cargar métodos de envío y datos de la empresa dinámicamente desde la API
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingMethods(true);

        // Cargar métodos de envío
        const methodsResponse = await fetch('/api/delivery-methods/active');

        if (methodsResponse.ok) {
          const methodsData = await methodsResponse.json();

          if (methodsData.success && methodsData.data) {
            // Formatear los datos para el dropdown
            const formattedMethods = methodsData.data.map(method => ({
              value: method.code,
              label: method.name
            }));

            setShippingMethods(formattedMethods);
          }
        } else {
          console.error('Error cargando métodos de envío');
          // Fallback: usar algunos métodos básicos
          setShippingMethods([
            { value: 'recogida_tienda', label: 'Recoge en Bodega' },
            { value: 'domicilio', label: 'Domicilio' },
            { value: 'envio_nacional', label: 'Envío Nacional' }
          ]);
        }

        // Cargar datos de la empresa
        const companyResponse = await fetch('/api/company-config/public');

        if (companyResponse.ok) {
          const companyData = await companyResponse.json();

          if (companyData.success && companyData.data) {
            setCompanyData(companyData.data);
          } else {
            // Fallback: datos por defecto
            setCompanyData({
              company_name: 'PERLAS EXPLOSIVAS COLOMBIA',
              nit: '901749888',
              address: 'CALLE 50 # 31-46',
              whatsapp: '3105244298',
              email: 'COMERCIAL@PERLAS-EXPLOSIVAS.COM',
              city: 'medellin',
              department: 'Antioquia'
            });
          }
        } else {
          console.error('Error cargando datos de la empresa');
          // Fallback: datos por defecto
          setCompanyData({
            company_name: 'PERLAS EXPLOSIVAS COLOMBIA',
            nit: '901749888',
            address: 'CALLE 50 # 31-46',
            whatsapp: '3105244298',
            email: 'COMERCIAL@PERLAS-EXPLOSIVAS.COM',
            city: 'medellin',
            department: 'Antioquia'
          });
        }

      } catch (error) {
        console.error('Error cargando datos:', error);

        // Fallback para métodos de envío
        setShippingMethods([
          { value: 'recogida_tienda', label: 'Recoge en Bodega' },
          { value: 'domicilio', label: 'Domicilio' },
          { value: 'envio_nacional', label: 'Envío Nacional' }
        ]);

        // Fallback para datos de la empresa
        setCompanyData({
          company_name: 'PERLAS EXPLOSIVAS COLOMBIA',
          nit: '901749888',
          address: 'CALLE 50 # 31-46',
          whatsapp: '3105244298',
          email: 'COMERCIAL@PERLAS-EXPLOSIVAS.COM',
          city: 'medellin',
          department: 'Antioquia'
        });

      } finally {
        setLoadingMethods(false);
      }
    };

    // Solo cargar cuando el modal esté abierto
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Estado para transportadoras dinámicas con el objeto completo (id + name)
  const [transportCompanies, setTransportCompanies] = useState([]);
  const [carriersList, setCarriersList] = useState([]); // Lista completa de carriers con ID y nombre

  // Cargar transportadoras dinámicamente desde la API - COMPLETAMENTE DINÁMICO
  React.useEffect(() => {
    const fetchCarriers = async () => {
      try {
        // Cargar desde el endpoint público de transportadoras activas
        const response = await fetch('/api/carriers/active');

        if (response.ok) {
          const result = await response.json();
          let carriers = [];

          // Si el response viene con success/data
          if (result.success && result.data) {
            carriers = result.data;
          }
          // Si el response es un array directo
          else if (Array.isArray(result)) {
            carriers = result;
          }

          // Siempre usar solo las transportadoras de la base de datos
          // No usar fallbacks hardcodeados para escalabilidad
          setCarriersList(carriers);

          // Extraer solo los nombres para el dropdown
          const carrierNames = carriers.map(c => c.name);
          setTransportCompanies(carrierNames);

          console.log(`✅ ${carrierNames.length} transportadoras cargadas desde la base de datos`);
          console.log('📦 Transportadoras disponibles:', carriers);

        } else {
          console.error('Error cargando transportadoras:', response.status);
          // No usar fallback hardcodeado - mantener lista vacía
          setCarriersList([]);
          setTransportCompanies([]);
          toast.error('No se pudieron cargar las transportadoras');
        }
      } catch (error) {
        console.error('Error cargando transportadoras:', error);
        // No usar fallback hardcodeado - mantener lista vacía
        setCarriersList([]);
        setTransportCompanies([]);
        toast.error('Error de conexión al cargar transportadoras');
      }
    };

    // Solo cargar cuando el modal esté abierto
    if (isOpen) {
      fetchCarriers();
    }
  }, [isOpen]);

  // Política: NO preseleccionar transportadora para evitar errores.
  // Requiere selección explícita del usuario en el dropdown.

  // Limpieza automática al cambiar el método de envío: evita que quede “pegada” una transportadora previa
  React.useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => {
      // Solo limpiar si hay un método seleccionado y existe algún valor previo
      if (prev.shippingMethod && (prev.transportCompany || prev.trackingNumber)) {
        return { ...prev, transportCompany: '', trackingNumber: '' };
      }
      return prev;
    });
  }, [formData.shippingMethod, isOpen]);



  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para imprimir voucher de pedido interno
  const handlePrintInternalVoucher = () => {
    if (internalOrders.length === 0) return;

    // Recopilar todos los items de todos los pedidos internos
    const allInternalItems = internalOrders.flatMap(io => io.items || []);

    // Obtener consecutivo(s) interno(s)
    const internalDisplayNumber = internalOrders.length === 1
      ? internalOrders[0].order_number
      : internalOrders.map(io => io.order_number).join(', ');

    // Usar la utilidad, pasando un objeto híbrido para que salga el consecutivo correcto
    const orderForPrint = {
      ...order,
      order_number: internalDisplayNumber, // Override order number with INT-XXXX
      related_external_order: order.order_number, // Pass original parent number
      // Preserve customer info from parent
      customer_name: extractedData?.name || order.customer_name || 'Cliente Particular'
    };

    printInternalVoucher(orderForPrint, allInternalItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shippingMethod) {
      toast.error('Debe seleccionar un método de envío');
      return;
    }
    // Requerir transportadora solo para métodos que NO son locales ni 'recoge_bodega'
    if (formData.shippingMethod && !isLocalMethod(formData.shippingMethod) && formData.shippingMethod !== 'recoge_bodega' && !formData.transportCompany) {
      toast.error('Debe seleccionar una transportadora');
      return;
    }

    // Validación y persistencia del valor de domicilio cuando aplica
    const isLocal = isLocalMethod(formData.shippingMethod);
    const isContraentrega = (formData.shippingPaymentMethod || '').toLowerCase() === 'contraentrega';

    if (isLocal && isContraentrega && !formData.deliveryFeeExempt) {
      const fee = Number(formData.deliveryFee || 0);
      if (!fee || fee <= 0) {
        toast.error('Debe ingresar el valor del domicilio a cobrar');
        return;
      }
    }


    try {
      setLoading(true);

      // Si aplica, actualizar el pedido con el valor de domicilio y exención
      if (isLocal && isContraentrega) {
        try {
          await orderService.updateOrder(order.id, {
            delivery_fee: Number(formData.deliveryFee || 0),
            delivery_fee_exempt: Boolean(formData.deliveryFeeExempt)
          });
        } catch (err) {
          console.error('Error guardando valor de domicilio:', err);
          toast.error('No se pudo guardar el valor de domicilio');
          setLoading(false);
          return;
        }
      }

      const processData = {
        orderId: order.id,
        shippingMethod: formData.shippingMethod,
        transportCompany: formData.transportCompany,
        trackingNumber: formData.trackingNumber,
        shippingPaymentMethod: formData.shippingPaymentMethod,
        assignedMessenger: formData.assignedMessenger,
        notes: formData.notes
      };

      await onProcess(processData);

      toast.success('Pedido procesado exitosamente');
      onClose();

      // Reset form
      setFormData({
        shippingMethod: '',
        transportCompany: '',
        trackingNumber: '',
        assignedMessenger: '',
        shippingPaymentMethod: 'contado',
        deliveryFee: '',
        deliveryFeeExempt: false,
        notes: ''
      });

    } catch (error) {
      console.error('Error procesando pedido:', error);
      toast.error(error.message || 'Error procesando pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateGuide = async () => {
    if (!formData.shippingMethod) {
      toast.error('Debe seleccionar un método de envío primero');
      return;
    }
    if (!isLocalMethod(formData.shippingMethod) && formData.shippingMethod !== 'recoge_bodega' && !formData.transportCompany) {
      toast.error('Debe seleccionar una transportadora');
      return;
    }

    try {
      setLoading(true);

      // Crear formulario para enviar datos por POST a nueva ventana
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/logistics/generate-guide-html';
      form.target = '_blank';

      // Datos para enviar (priorizar datos extraídos de Notas/Observaciones)
      const combinedNotes = [order.siigo_observations, formData.notes].filter(Boolean).join('\n');
      const formData_guide = {
        orderId: order.id,
        shippingMethod: formData.shippingMethod,
        transportCompany: formData.transportCompany,
        customerName: extractedData?.name || order.customer_name,
        customerPhone: extractedData?.phone || order.customer_phone,
        customerAddress: extractedData?.address || order.customer_address,
        customerCity: extractedData?.city || order.customer_city,
        customerDepartment: extractedData?.department || order.customer_department,
        notes: combinedNotes
      };

      // Agregar campos al formulario
      Object.keys(formData_guide).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = formData_guide[key] || '';
        form.appendChild(input);
      });

      // Enviar formulario
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      toast.success('Guía abierta en nueva ventana. Use Ctrl+P para imprimir o guardar como PDF');

    } catch (error) {
      console.error('Error generando guía:', error);
      toast.error('Error generando guía de envío');
    } finally {
      setLoading(false);
    }
  };

  // Indicadores de pago para mostrar estado claro del monto
  const isTransfer = normalize(order?.payment_method) === 'transferencia';
  const walletApproved = order?.validation_status === 'approved';

  // Distribución derivada cuando los ítems no traen precios desde backend
  const derivedBreakdown = React.useMemo(() => {
    try {
      const items = Array.isArray(order?.items) ? order.items : [];
      if (!items.length) return null;
      const anyVal = items.some((it) => getUnitPrice(it) > 0 || getSubtotal(it) > 0);
      const total = toMoneyNumber(order?.total_amount ?? order?.total);
      if (anyVal || !(total > 0)) return null;
      const sumQty = items.reduce((s, it) => s + (Number(it?.quantity || 1) || 1), 0) || 1;
      const breakdown = [];
      let allocated = 0;
      items.forEach((it, idx) => {
        const qty = Number(it?.quantity || 1) || 1;
        const raw = Math.round((total * qty) / sumQty);
        breakdown[idx] = { unit: Math.round(raw / qty), subtotal: raw };
        allocated += raw;
      });
      if (breakdown.length) {
        const diff = total - allocated;
        breakdown[breakdown.length - 1].subtotal += diff;
        const qtyLast = Number(items[items.length - 1]?.quantity || 1) || 1;
        breakdown[breakdown.length - 1].unit = Math.round(breakdown[breakdown.length - 1].subtotal / qtyLast);
      }
      return breakdown;
    } catch {
      return null;
    }
  }, [order]);

  // Fallback seguro para render: si extractedData aún es null en este render,
  // calcular en caliente desde Observaciones/Notas para que la Vista Previa no quede desfasada.
  const effectiveData = React.useMemo(() => {
    try {
      return extractedData || extractRecipientDataFromNotes([order?.siigo_observations, order?.notes].filter(Boolean).join('\n')) || null;
    } catch {
      return extractedData || null;
    }
  }, [extractedData, order?.siigo_observations, order?.notes]);

  const cityToShow = effectiveData?.city || order?.customer_city || '';
  const deptToShow = effectiveData?.department || order?.customer_department || '';

  // Debug: verificar qué se está renderizando en la Vista Previa
  console.log(
    'LogisticsModal: effectiveData',
    effectiveData,
    { customer_city: order?.customer_city, customer_department: order?.customer_department },
    'order.siigo_observations:',
    order?.siigo_observations,
    'order.notes:',
    order?.notes
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-[95vw] w-full mx-4 max-h-[90vh] flex flex-col" style={{ zIndex: 10000 }}>
        {/* Header - Fixed Top */}
        <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200 rounded-t-lg shrink-0 gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="shrink-0">
                <h2 className="text-lg font-bold text-gray-500 uppercase tracking-wider mb-1">Procesar Envío</h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-blue-900 tracking-tight">#{order?.order_number}</span>
                  </div>
                  <div className="text-xl font-bold text-gray-800 border-l-2 border-gray-300 pl-4 py-0.5">
                    {order?.customer_name}
                  </div>
                </div>
              </div>

              {/* Customer Details Block - Header */}
              <div className="flex flex-wrap items-center gap-3 lg:gap-6 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                    <Icons.Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Teléfono</p>
                    <p className="font-bold text-gray-900 text-sm">{order?.customer_phone}</p>
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-200 hidden lg:block"></div>
                <div className="flex items-center gap-3 max-w-[300px]">
                  <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                    <Icons.MapPin className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-500 font-bold uppercase">Ubicación</p>
                    <p className="font-bold text-gray-900 text-sm leading-tight truncate" title={order?.customer_address}>{order?.customer_address}</p>
                    <p className="text-xs text-gray-600 truncate">{cityToShow}, {deptToShow}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2"
          >
            <Icons.X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable Area */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 bg-gray-100/50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

            {/* COLUMNA 1: Contexto e Información (Span 4) */}
            <div className="lg:col-span-4 space-y-3">

              {/* Card Principal: Datos del Pedido */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                  <h3 className="font-bold text-gray-800 text-base flex items-center">
                    <Icons.Info className="w-5 h-5 mr-2 text-blue-600" />
                    Detalles del Pedido
                  </h3>
                  <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold font-mono">
                    #{order?.order_number}
                  </span>
                </div>

                {/* Datos del Cliente */}
                {/* Datos del Cliente MOVIDOS AL HEADER */}
                <div className="hidden">
                  {/* Se mantiene este div vacío o se elimina para no romper estructura si algo depende de él, 
                      pero visualmente ya está en el header */}
                </div>

                {/* Resumen Financiero en Mini-Card */}
                <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-100">
                  {/* Desglose de Pagos - Backend calculado */}
                  {(() => {
                    const internalTotal = internalOrders.reduce((sum, intOrder) => sum + Number(intOrder.total_amount || 0), 0);
                    const siigoTotal = toMoneyNumber(order?.total_amount);
                    const total = siigoTotal + internalTotal;
                    const paid = toMoneyNumber(order?.paid_amount);

                    // Calcular pendiente
                    const pending = order?.payment_pending !== undefined
                      ? toMoneyNumber(order?.payment_pending)
                      : Math.max(0, total - paid);

                    return (
                      <div className="space-y-2">
                        {/* Breakdown */}
                        <div className="space-y-1 pb-2 border-b border-gray-200">
                          <div className="flex justify-between items-baseline text-xs text-gray-500">
                            <span>Pedido Siigo</span>
                            <span className="font-medium text-gray-700">${Number(siigoTotal).toLocaleString('es-CO')}</span>
                          </div>
                          {internalTotal > 0 && (
                            <div className="flex justify-between items-baseline text-xs text-orange-600 font-medium">
                              <span>+ Internos ({internalOrders.length})</span>
                              <span>${Number(internalTotal).toLocaleString('es-CO')}</span>
                            </div>
                          )}
                        </div>

                        {/* Grand Total */}
                        <div>
                          <p className="text-xs text-gray-500 font-medium uppercase text-right mb-0.5">Total a Pagar</p>
                          <div className="text-right">
                            <span className="text-3xl font-black text-green-600 tracking-tight">
                              ${Number(total).toLocaleString('es-CO')}
                            </span>
                          </div>
                        </div>

                        {/* Payments / Pending */}
                        {paid > 0 && (
                          <div className="pt-2 border-t border-gray-200 space-y-1">
                            <div className="flex justify-between items-baseline text-sm text-green-700">
                              <span className="flex items-center"><Icons.CheckCircle2 className="w-3 h-3 mr-1" /> Abonado</span>
                              <span className="font-bold">-${Number(paid).toLocaleString('es-CO')}</span>
                            </div>
                            {pending > 0 ? (
                              <div className="flex justify-between items-baseline text-base font-bold text-red-600 pt-1">
                                <span>Pendiente</span>
                                <span>${Number(pending).toLocaleString('es-CO')}</span>
                              </div>
                            ) : (
                              <div className="text-center text-sm font-bold text-green-600 bg-green-50 rounded py-1.5 mt-1 border border-green-100">
                                ¡Pagado Totalmente!
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="mt-3 flex flex-col items-end gap-2 border-t border-gray-200 pt-2">
                    <div className="flex items-center justify-between w-full text-xs">
                      <span className="text-gray-500 font-medium">Método Pago:</span>
                      <span className={`font-bold text-sm ${getPaymentBadgeClass(order?.payment_method || extractedData?.paymentMethod || 'auto')}`}>
                        {getPaymentMethodLabel(order?.payment_method || extractedData?.paymentMethod)}
                      </span>
                    </div>
                    {isTransfer && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getPaymentBadgeClass('transferencia')}`}>
                        Transferencia
                      </span>
                    )}
                    {walletApproved && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                        <Icons.CheckCircle2 className="w-3 h-3 mr-1" />
                        Validado Cartera
                      </span>
                    )}
                  </div>
                </div>

                {/* Link Factura */}
                {order?.siigo_public_url && (
                  <div className="mt-4">
                    <a
                      href={order.siigo_public_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center w-full bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold py-2.5 rounded-lg transition-all border border-blue-200 hover:border-blue-300 uppercase tracking-wide"
                    >
                      <Icons.FileText className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                      Ver Factura SIIGO
                    </a>
                  </div>
                )}
              </div>

              {/* Datos del Destinatario Extraídos */}
              {extractedData && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm">
                  <h3 className="font-bold text-green-900 mb-3 flex items-center text-sm border-b border-green-200 pb-2">
                    <Icons.Scan className="w-4 h-4 mr-2" />
                    Datos Detectados (Para Guía)
                  </h3>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-700 font-medium">Nombre:</span>
                      <span className="text-green-900 text-right font-medium">{extractedData.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700 font-medium">Teléfono:</span>
                      <span className="text-green-900 text-right font-medium">{extractedData.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700 font-medium">Ciudad:</span>
                      <span className="text-green-900 text-right font-medium">{extractedData.city}</span>
                    </div>
                    <div className="mt-1 pt-1 border-t border-green-200">
                      <span className="text-green-700 font-medium block">Dirección:</span>
                      <p className="text-green-900 break-words font-medium">{extractedData.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* COLUMNA 2: Productos y Notas (Span 4) */}
            <div className="lg:col-span-4 space-y-4">
              {/* Lista de Items SIIGO (Siempre visible si hay espacio) */}
              {order?.items && order.items.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-800 flex items-center">
                      <Icons.Package className="w-5 h-5 mr-2 text-gray-500" />
                      Productos SIIGO ({order.items.length})
                    </span>
                    {/* Toggle button if needed, but defaults to open */}
                  </div>

                  <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10 text-xs">
                        <tr>
                          <th className="px-4 py-2 text-left font-bold text-gray-500 uppercase tracking-wider">Producto</th>
                          <th className="px-2 py-2 text-center font-bold text-gray-500 uppercase tracking-wider">Cant</th>
                          <th className="px-4 py-2 text-right font-bold text-gray-500 uppercase tracking-wider">$$</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {order.items.map((item, index) => (
                          <tr key={`siigo-${index}`} className="hover:bg-gray-50 group">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-bold text-gray-800 leading-tight text-sm">{item.name}</p>
                                {/* Small description if available */}
                                {item.product_code && <span className="text-xs text-gray-400 font-mono mt-0.5 block">{item.product_code}</span>}
                              </div>
                            </td>
                            <td className="px-2 py-3 text-center whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 shadow-sm">
                                {item.quantity} {item.unit || 'und'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900 text-sm">
                              ${(getSubtotal(item) || derivedBreakdown?.[index]?.subtotal || 0).toLocaleString('es-CO')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Lista de Items INTERNOS */}
              {internalOrders.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 border-b border-orange-200 bg-orange-100 flex items-center justify-between">
                    <span className="text-sm font-bold text-orange-900 flex items-center">
                      <Icons.PackagePlus className="w-5 h-5 mr-2" />
                      Productos Internos
                    </span>
                    {/* Botón Imprimir Voucher */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintInternalVoucher();
                      }}
                      className="p-1.5 text-orange-700 hover:text-orange-900 hover:bg-orange-200 rounded-full transition-colors"
                      title="Imprimir Voucher Interno"
                    >
                      <Icons.Printer className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-100/50 sticky top-0 z-10 text-xs">
                        <tr>
                          <th className="px-4 py-2 text-left font-bold text-orange-800 uppercase tracking-wider">Producto</th>
                          <th className="px-2 py-2 text-center font-bold text-orange-800 uppercase tracking-wider">Cant</th>
                          <th className="px-4 py-2 text-right font-bold text-orange-800 uppercase tracking-wider">$$</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-orange-200/50">
                        {internalOrders.flatMap(io => io.items || []).map((item, index) => (
                          <tr key={`int-${index}`} className="hover:bg-orange-100/50">
                            <td className="px-4 py-3">
                              <p className="font-bold text-gray-900 leading-tight text-sm">{item.name}</p>
                            </td>
                            <td className="px-2 py-3 text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                {item.quantity} {item.unit || 'und'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-orange-900 text-sm">
                              ${(Number(item.total || item.subtotal || (item.price * item.quantity) || 0).toLocaleString('es-CO'))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notas de la Factura SIIGO */}
              {(order?.siigo_observations || order?.notes) && (
                <div className="bg-blue-50/50 rounded-lg border border-blue-100">
                  <div
                    className="w-full px-4 py-2 flex items-center justify-between bg-blue-50/80 rounded-t-lg"
                  >
                    <span className="font-bold text-xs text-blue-900 flex items-center uppercase tracking-wide">
                      <Icons.FileText className="w-3 h-3 mr-2" />
                      Notas Importantes
                    </span>
                  </div>

                  <div className="px-3 pb-3 pt-2">
                    <div className="bg-white p-3 rounded border border-blue-100 shadow-sm max-h-[250px] overflow-y-auto custom-scrollbar">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                        {(() => {
                          let textToFormat = order.siigo_observations || order.notes || '';
                          if (order.siigo_observations) {
                            const fieldsToSeparate = [
                              'OBSERVACIONES SIIGO:', 'ESTADO DE PAGO:', 'MEDIO DE PAGO:',
                              'FORMA DE PAGO DE ENVIO:', 'NOMBRE:', 'NIT:', 'TELÉFONO:',
                              'DEPARTAMENTO:', 'CIUDAD:', 'DIRECCIÓN:', 'NOTA:'
                            ];
                            fieldsToSeparate.forEach(field => {
                              const pattern = new RegExp(`([^\\n])${field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
                              textToFormat = textToFormat.replace(pattern, `$1\n${field}`);
                            });
                            textToFormat = textToFormat.replace(/\r\n/g, '\n').split('\n').map(line => line.trim()).filter(Boolean).join('\n');
                          }
                          return textToFormat;
                        })()}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* COLUMNA 3: Gestión de Envío (Span 4) */}
            <div className="lg:col-span-4 space-y-4">
              {/* Bloque de Inputs de Envío */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-5 relative">
                <div className="absolute top-0 right-0 bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-bl-lg rounded-tr-lg">
                  CONFIGURACIÓN
                </div>
                {/* Método de envío */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                    1. Método de Envío *
                  </label>
                  <CustomDropdown
                    value={formData.shippingMethod}
                    onChange={(value) => handleInputChange('shippingMethod', value)}
                    options={shippingMethods}
                    placeholder="Seleccionar..."
                    required
                  />
                </div>

                {/* Transportadora */}
                {formData.shippingMethod && formData.shippingMethod !== 'recoge_bodega' && !isLocalMethod(formData.shippingMethod) && (
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                      2. Transportadora <span className="text-red-600">*</span>
                    </label>
                    <CustomDropdown
                      value={formData.transportCompany}
                      onChange={(value) => handleInputChange('transportCompany', value)}
                      options={transportCompanies.map(company => ({ value: company, label: company }))}
                      placeholder="Seleccione..."
                      required
                    />
                  </div>
                )}

                {/* Número de guía */}
                {formData.transportCompany && formData.transportCompany !== 'Mensajería Local' && (
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                      3. Número de Guía
                    </label>
                    <input
                      type="text"
                      value={formData.trackingNumber}
                      onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                      placeholder="# Guía"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                    />
                  </div>
                )}
              </div>

              {/* Vista previa de la guía */}
              {formData.shippingMethod && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col">
                  <h4 className="font-bold text-gray-700 mb-3 flex items-center shrink-0 text-sm">
                    <Icons.Eye className="w-5 h-5 mr-2 text-gray-500" />
                    Vista Previa Guía
                  </h4>

                  <div className="bg-white rounded-lg border overflow-hidden shadow-sm">
                    <table className="w-full text-sm">
                      <tbody>
                        {/* Remitente Compacto */}
                        <tr className="bg-gray-50"><td colSpan="2" className="px-3 py-1.5 font-bold text-gray-500 text-[10px] border-b">REMITENTE</td></tr>
                        <tr>
                          <td className="px-3 py-1.5 text-gray-500 w-24 text-xs font-medium uppercase">Empresa:</td>
                          <td className="px-3 py-1.5 font-medium">{companyData?.company_name || '...'}</td>
                        </tr>
                        {/* Destinatario Compacto */}
                        <tr className="bg-gray-50"><td colSpan="2" className="px-3 py-1.5 font-bold text-gray-500 text-[10px] border-b border-t">DESTINATARIO</td></tr>
                        <tr>
                          <td className="px-3 py-1.5 text-gray-500 text-xs font-medium uppercase">Nombre:</td>
                          <td className="px-3 py-1.5 font-bold text-gray-900">{effectiveData?.name || order?.customer_name}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-1.5 text-gray-500 text-xs font-medium uppercase">Dirección:</td>
                          <td className="px-3 py-1.5 break-words max-w-[200px]">{effectiveData?.address || order?.customer_address}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-1.5 text-gray-500 text-xs font-medium uppercase">Teléfono:</td>
                          <td className="px-3 py-1.5">{effectiveData?.phone || order?.customer_phone}</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-1.5 text-gray-500 text-xs font-medium uppercase">Ciudad:</td>
                          <td className="px-3 py-1.5">{cityToShow}, {deptToShow}</td>
                        </tr>
                        {/* Envío Compacto */}
                        <tr className="bg-yellow-50"><td colSpan="2" className="px-3 py-2 font-bold text-yellow-800 text-xs border-b border-t">ENVÍO: {getShippingPayLabel(order?.shipping_payment_method || effectiveData?.shippingPaymentMethod || formData.shippingPaymentMethod)}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Botón para generar PDF */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleGenerateGuide}
                      disabled={loading}
                      className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg flex items-center justify-center space-x-2 text-sm font-bold shadow-sm transition-all"
                    >
                      <Icons.Download className="w-5 h-5" />
                      <span>Descargar PDF</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </form>

        {/* Footer - Sticky Bottom */}
        <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg flex justify-between items-center shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 transition-all duration-300">
          <div className="text-xs text-gray-500 italic hidden sm:block">
            * Verifique los datos antes de procesar
          </div>
          <div className="flex space-x-3 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-bold text-sm transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm shadow-lg hover:shadow-blue-500/30 transition-all flex items-center transform hover:-translate-y-0.5"
              disabled={loading || !formData.shippingMethod}
            >
              {loading ? (
                <>
                  <Icons.Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Icons.Check className="w-5 h-5 mr-2" />
                  PROCESAR ENVÍO
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsModal;
